/**
 * Complete Your Stack - JavaScript
 * Handles checkbox toggling, variant selection, and batch add to cart
 */

class CompleteYourStack {
  constructor(section) {
    this.section = section;
    this.addAllBtn = section.querySelector('[data-stack-add-all]');
    this.itemCountEl = section.querySelector('[data-item-count]');
    this.totalPriceEl = section.querySelector('[data-total-price]');
    this.productCards = section.querySelectorAll('[data-stack-item]');
    
    // Check for theme's event helper
    this.EVENT_HELPER = window._EVENT_HELPER || {
      triggerCartChange: 'action:cart:updateCart',
      showDrawer: 'action:drawer:show'
    };
    
    this.init();
  }

  init() {
    // Initialize each product card
    this.productCards.forEach(card => {
      this.initProductCard(card);
    });

    // Add event listener to "Add All" button
    if (this.addAllBtn) {
      this.addAllBtn.addEventListener('click', () => this.addAllToCart());
    }

    // Initialize all checkboxes with their data from the DOM
    this.initializeCheckboxData();

    // Initial update of button state (AFTER initializing checkbox data)
    this.updateButtonState();
  }

  initializeCheckboxData() {
    // Read the initial data-variant-id and data-product-price from DOM
    // This ensures the button works on page load without requiring a variant change
    const checkboxes = this.section.querySelectorAll('[data-stack-checkbox]');
    
    checkboxes.forEach(checkbox => {
      // Get initial values from DOM attributes
      const variantId = checkbox.getAttribute('data-variant-id');
      const productPrice = checkbox.getAttribute('data-product-price');
      
      if (variantId) {
        checkbox.dataset.variantId = variantId;
      }
      
      if (productPrice) {
        checkbox.dataset.productPrice = productPrice;
      }

      console.log('Initialized checkbox:', {
        variantId: checkbox.dataset.variantId,
        price: checkbox.dataset.productPrice,
        checked: checkbox.checked,
        disabled: checkbox.disabled
      });
    });
  }

  initProductCard(card) {
    const checkbox = card.querySelector('[data-stack-checkbox]');
    const productContent = card.querySelector('.stack-product-content');
    const variantSelects = card.querySelectorAll('[data-option-select]');
    
    // Toggle checkbox when clicking product image/content
    if (productContent && checkbox && !checkbox.disabled) {
      productContent.addEventListener('click', (e) => {
        // Don't toggle if clicking on variant selects or links
        if (e.target.closest('select') || e.target.closest('a')) {
          return;
        }
        checkbox.checked = !checkbox.checked;
        this.updateButtonState();
      });
    }

    // Update button state when checkbox changes
    if (checkbox) {
      checkbox.addEventListener('change', () => {
        this.updateButtonState();
      });
    }

    // Handle variant selection
    if (variantSelects.length > 0) {
      this.initVariantSelectors(card, variantSelects);
    }
  }

  initVariantSelectors(card, selects) {
    const variantsData = card.querySelector('[data-product-variants]');
    if (!variantsData) return;

    let variants;
    try {
      variants = JSON.parse(variantsData.textContent);
    } catch (e) {
      console.error('Error parsing variants data:', e);
      return;
    }

    const checkbox = card.querySelector('[data-stack-checkbox]');
    const priceElement = card.querySelector('.stack-price-regular');
    const comparePriceElement = card.querySelector('.stack-price-compare');
    const badgeContainer = card.querySelector('[data-badge-container]');
    const productImage = card.querySelector('.stack-product-image img');

    selects.forEach(select => {
      select.addEventListener('change', () => {
        // Get all selected options
        const selectedOptions = Array.from(selects).map(s => s.value);
        
        // Find matching variant
        const matchingVariant = variants.find(variant => {
          return selectedOptions.every((option, index) => {
            return variant.options[index] === option;
          });
        });

        if (matchingVariant) {
          console.log('Variant changed to:', matchingVariant);

          // Update checkbox data attributes
          if (checkbox) {
            checkbox.dataset.variantId = matchingVariant.id;
            checkbox.dataset.productPrice = matchingVariant.price;
            
            // Update availability
            if (matchingVariant.available) {
              checkbox.disabled = false;
              if (!checkbox.checked) {
                checkbox.checked = true; // Auto-select when variant changes
              }
            } else {
              checkbox.disabled = true;
              checkbox.checked = false;
            }
          }

          // Update product image if variant has a featured image
          if (productImage && matchingVariant.featured_image) {
            const newImageUrl = matchingVariant.featured_image.src;
            productImage.src = newImageUrl;
            productImage.srcset = newImageUrl; // Update srcset too
            productImage.alt = matchingVariant.featured_image.alt || matchingVariant.name;
            
            console.log('Updated image to:', newImageUrl);
          }

          // Update displayed price
          if (priceElement) {
            priceElement.textContent = this.formatMoney(matchingVariant.price);
            
            // Update sale styling
            if (matchingVariant.compare_at_price && matchingVariant.compare_at_price > matchingVariant.price) {
              priceElement.classList.add('stack-price-sale');
              if (comparePriceElement) {
                comparePriceElement.textContent = this.formatMoney(matchingVariant.compare_at_price);
                comparePriceElement.style.display = '';
              }
            } else {
              priceElement.classList.remove('stack-price-sale');
              if (comparePriceElement) {
                comparePriceElement.style.display = 'none';
              }
            }
          }

          // Update sold-out badge visibility
          this.updateBadgeVisibility(card, matchingVariant);

          // Update button state
          this.updateButtonState();
        }
      });
    });
  }

  updateBadgeVisibility(card, variant) {
    const badgeContainer = card.querySelector('[data-badge-container]');
    if (!badgeContainer) return;

    // Find or create sold-out badge
    let soldOutBadge = badgeContainer.querySelector('[data-sold-out-badge]');
    
    if (!variant.available) {
      // Show sold-out badge
      if (!soldOutBadge) {
        // Create badge if it doesn't exist
        soldOutBadge = document.createElement('span');
        soldOutBadge.className = 'p_badge out_of_stock';
        soldOutBadge.setAttribute('data-sold-out-badge', '');
        soldOutBadge.textContent = 'Sold Out'; // You may want to use theme translation
        badgeContainer.prepend(soldOutBadge);
      }
      soldOutBadge.style.display = '';
      
      // Hide other badges when sold out
      const otherBadges = badgeContainer.querySelectorAll('.p_badge:not([data-sold-out-badge])');
      otherBadges.forEach(badge => {
        badge.style.display = 'none';
      });
    } else {
      // Hide sold-out badge
      if (soldOutBadge) {
        soldOutBadge.style.display = 'none';
      }
      
      // Show other badges
      const otherBadges = badgeContainer.querySelectorAll('.p_badge:not([data-sold-out-badge])');
      otherBadges.forEach(badge => {
        badge.style.display = '';
      });
    }
  }

  updateButtonState() {
    const checkedBoxes = this.section.querySelectorAll('[data-stack-checkbox]:checked:not(:disabled)');
    const count = checkedBoxes.length;
    
    // Calculate total price
    let totalPrice = 0;
    checkedBoxes.forEach(checkbox => {
      const price = parseInt(checkbox.dataset.productPrice) || 0;
      totalPrice += price;
    });

    // Update button text
    if (this.itemCountEl) {
      this.itemCountEl.textContent = count;
    }
    
    if (this.totalPriceEl) {
      this.totalPriceEl.textContent = this.formatMoney(totalPrice);
    }

    // Enable/disable button
    if (this.addAllBtn) {
      if (count > 0) {
        this.addAllBtn.disabled = false;
        this.addAllBtn.querySelector('[data-button-text]').textContent = 
          `Add ${count} item${count > 1 ? 's' : ''} to cart - ${this.formatMoney(totalPrice)}`;
      } else {
        this.addAllBtn.disabled = true;
        this.addAllBtn.querySelector('[data-button-text]').textContent = 
          'Select products to add';
      }
    }
  }

  async addAllToCart() {
    const checkedBoxes = this.section.querySelectorAll('[data-stack-checkbox]:checked:not(:disabled)');
    
    if (checkedBoxes.length === 0) return;

    // Set loading state
    this.addAllBtn.classList.add('loading');
    this.addAllBtn.disabled = true;

    // Prepare items array
    const items = Array.from(checkedBoxes).map(checkbox => {
      const variantId = checkbox.dataset.variantId || checkbox.getAttribute('data-variant-id');
      if (!variantId) {
        console.error('No variant ID found for checkbox:', checkbox);
      }
      return {
        id: variantId,
        quantity: 1
      };
    }).filter(item => item.id); // Remove any items without IDs

    try {
      // Validate items
      if (items.length === 0) {
        console.error('No valid items to add to cart');
        this.showErrorMessage();
        return;
      }

      console.log('Adding items to cart:', items); // Debug log

      // Add all items to cart
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ items })
      });

      if (!response.ok) {
        throw new Error('Failed to add items to cart');
      }

      const result = await response.json();
      
      // Update cart and open drawer (using your theme's system)
      this.triggerCartUpdate();
      
      // Show success message (optional, since drawer opens)
      this.showSuccessMessage(checkedBoxes.length);
      
      // Uncheck all boxes after successful add
      checkedBoxes.forEach(checkbox => {
        checkbox.checked = false;
      });
      
      this.updateButtonState();

    } catch (error) {
      console.error('Error adding to cart:', error);
      this.showErrorMessage();
    } finally {
      // Remove loading state
      this.addAllBtn.classList.remove('loading');
      this.addAllBtn.disabled = false;
    }
  }

  triggerCartUpdate() {
    // Get sections to render (matching your theme's cart system)
    const sectionsToRender = this.getSectionsToRender();
    
    // Fetch updated cart data with sections
    fetch(`${window.Shopify.routes.root}cart/update.js?sections=${sectionsToRender}`)
      .then(response => response.json())
      .then(data => {
        console.log('Cart updated with sections:', data);
        
        // Update cart count immediately (backup method)
        this.updateCartCount(data.item_count);
        
        // Trigger your theme's cart update event
        // This dispatches to DrawerCart which listens for 'action:cart:updateCart'
        document.dispatchEvent(
          new CustomEvent(this.EVENT_HELPER.triggerCartChange, {
            detail: { sections: data.sections }
          })
        );
        
        // Open the cart drawer using theme's drawer system
        // This triggers the drawer to slide out
        if (this.EVENT_HELPER.showDrawer) {
          document.dispatchEvent(
            new CustomEvent(this.EVENT_HELPER.showDrawer, {
              detail: { drawerType: 'cart-drawer' }
            })
          );
        }
        
        // Legacy support for other cart systems
        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh'));
        document.documentElement.dispatchEvent(
          new CustomEvent('cart:updated', { detail: { cart: data } })
        );
      })
      .catch(error => {
        console.error('Error updating cart:', error);
      });
  }

  updateCartCount(itemCount) {
    // Update all cart count elements directly
    // This ensures the count updates even if section rendering doesn't work
    
    // Common cart count selectors
    const selectors = [
      '.header__cart__icon-count',
      '.cart-count-bubble span:not(.visually-hidden)',
      '[header-cart-bubble] .header__cart__icon-count',
      'cart-count',
      '.cart-count'
    ];
    
    selectors.forEach(selector => {
      const elements = document.querySelectorAll(selector);
      elements.forEach(el => {
        el.textContent = itemCount;
        console.log('Updated cart count element:', selector, itemCount);
      });
    });
    
    // Show/hide cart count bubble based on item count
    const bubbles = document.querySelectorAll('[header-cart-bubble], .cart-count-bubble');
    bubbles.forEach(bubble => {
      if (itemCount > 0) {
        bubble.style.display = '';
      } else {
        bubble.style.display = 'none';
      }
    });
  }

  getSectionsToRender() {
    // Try to use theme's section rendering utility if available
    if (window.HELPER_UTIL && window.HELPER_UTIL.sectionRenderUtil) {
      try {
        const sectionsString = window.HELPER_UTIL.sectionRenderUtil('cart-drawer').getSectionsString();
        console.log('Using theme sections:', sectionsString);
        return sectionsString;
      } catch (e) {
        console.log('Could not use theme section utility, using fallback');
      }
    }
    
    // Fallback: Common section names
    // The header section typically contains cart icon/bubble
    const sections = [
      'cart-drawer',
      'header'
    ];
    
    return sections.join(',');
  }

  showSuccessMessage(count) {
    // Create a simple success notification
    const message = document.createElement('div');
    message.className = 'stack-success-message';
    message.textContent = `✓ Added ${count} item${count > 1 ? 's' : ''} to cart`;
    message.style.cssText = `
      position: fixed;
      bottom: 20px;
      right: 20px;
      background: #000;
      color: #fff;
      padding: 16px 24px;
      border-radius: 4px;
      font-size: 14px;
      font-weight: 600;
      z-index: 10000;
      animation: slideInUp 0.3s ease;
    `;

    document.body.appendChild(message);

    // Remove after 3 seconds
    setTimeout(() => {
      message.style.animation = 'slideOutDown 0.3s ease';
      setTimeout(() => message.remove(), 300);
    }, 3000);
  }

  showErrorMessage() {
    const message = document.createElement('div');
    message.className = 'stack-error-message';
    message.textContent = '✕ Failed to add items. Please try again.';
    message.style.cssText = `
      position: fixed;
      bottom: 20px;
      right: 20px;
      background: #e63946;
      color: #fff;
      padding: 16px 24px;
      border-radius: 4px;
      font-size: 14px;
      font-weight: 600;
      z-index: 10000;
      animation: slideInUp 0.3s ease;
    `;

    document.body.appendChild(message);

    setTimeout(() => {
      message.style.animation = 'slideOutDown 0.3s ease';
      setTimeout(() => message.remove(), 300);
    }, 3000);
  }

  formatMoney(cents) {
    // Use Shopify's money format if available
    if (window.Shopify && window.Shopify.formatMoney) {
      return window.Shopify.formatMoney(cents, window.theme?.moneyFormat || '${{amount}}');
    }
    
    // Fallback to basic formatting
    const dollars = (cents / 100).toFixed(2);
    return `$${dollars}`;
  }
}

// Add animation styles
const style = document.createElement('style');
style.textContent = `
  @keyframes slideInUp {
    from {
      transform: translateY(100%);
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }
  
  @keyframes slideOutDown {
    from {
      transform: translateY(0);
      opacity: 1;
    }
    to {
      transform: translateY(100%);
      opacity: 0;
    }
  }
`;
document.head.appendChild(style);

// Initialize on page load
document.addEventListener('DOMContentLoaded', () => {
  const sections = document.querySelectorAll('.complete-stack-section');
  sections.forEach(section => {
    new CompleteYourStack(section);
  });
});

// Re-initialize on Shopify section render (for theme editor)
if (window.Shopify && window.Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    const section = event.target.querySelector('.complete-stack-section');
    if (section) {
      new CompleteYourStack(section);
    }
  });
}